import React, { useState, useMemo, useEffect, useRef } from 'react';
import { AddressFinder } from './AddressFinder';
import { trackQuoteConversion } from '../lib/analytics';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ArrowRight, ArrowLeft, Sparkles, Loader2, CheckCircle2, Home as HomeIcon,
  Building2, Truck, Sofa, Zap, Trash2, Boxes, Key, MapPin, Calendar as CalIcon,
  Calendar, User, Phone, Mail, Package, Info, Plus, Minus, BedDouble, Refrigerator,
  WashingMachine, Tv, Armchair, PackageOpen, MessageCircle, Camera, Trees, Hammer,
  FileText, Monitor, ShoppingBag, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as ShadCalendar } from './ui/calendar';
import { BOOKING_URL } from '../mock';
import { DepositTransfer } from './DepositTransfer';
import { countOfferTap } from './OfferTaps';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import {
  SERVICES, isClearanceSvc, isOfficeSvc, isDeliverySvc,
  SIZES_MOVE, SIZES_CLEARANCE, ACCESS,
  MOVE_ITEMS, OFFICE_ITEMS, CLEARANCE_ITEMS, WASTE_LOCATIONS,
  BULKY, BULKY_CLEARANCE,
  VAN_CREW_OPTIONS, VAN_CREW_LABEL, STEP_COUNT, INITIAL_FORM,
  VOLUME_UNITS, computeVanFill,
  CAP_XLWB, CAP_LUTON, CAP_LUTON_PLUS, CAP_7_5T,
} from './EstimateWizard.constants';


const EstimateWizard = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const wizardRef = useRef(null);
  const isFirstRender = useRef(true);

  // Keep the wizard header in view when moving between steps (prevents the
  // page from "jumping down" when a new, taller step is rendered).
  useEffect(() => {
    if (isFirstRender.current === true || isFirstRender.current === step) {
      isFirstRender.current = step;
      return;
    }
    isFirstRender.current = step;
    if (wizardRef.current) {
      const rect = wizardRef.current.getBoundingClientRect();
      const absoluteTop = rect.top + window.pageYOffset;
      // Offset for sticky navbar / topbar (~120px)
      window.scrollTo({ top: absoluteTop - 120, behavior: 'smooth' });
    }
  }, [step]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateItem = (id, delta) =>
    setForm((f) => ({
      ...f,
      items: { ...f.items, [id]: Math.max(0, (f.items[id] || 0) + delta) },
    }));
  const toggleBulky = (item) =>
    setForm((f) => ({
      ...f,
      heavy_items: f.heavy_items.includes(item)
        ? f.heavy_items.filter((x) => x !== item)
        : [...f.heavy_items, item],
    }));

  const canNext = () => {
    if (step === 0) return !!form.service;
    if (step === 1) return !!form.property_size;
    if (step === 2 && isClearanceSvc(form.service)) {
      return !!form.waste_location;
    }
    if (step === 3) return !!form.van_crew;
    if (step === 4) {
      const pcShape = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i;
      const fromOk = pcShape.test((form.from_postcode || '').trim());
      if (isClearanceSvc(form.service)) return fromOk; // collection only
      return fromOk && pcShape.test((form.to_postcode || '').trim());
    }
    if (step === 5) return !!form.name && !!form.phone && !!form.email && /\S+@\S+\.\S+/.test(form.email || '');
    return true;
  };

  const next = () => {
    if (!canNext()) {
      if (step === 2 && isClearanceSvc(form.service)) {
        toast.error('Please tell us where the rubbish is.');
      } else if (step === 3) {
        toast.error('Please pick a van & crew, or choose "let us pick".');
      } else if (step === 4) {
        toast.error(isClearanceSvc(form.service)
          ? 'Please enter a valid UK collection postcode.'
          : 'Please enter valid UK postcodes for both pickup and delivery.');
      } else {
        toast.error('Please answer this step before continuing.');
      }
      return;
    }
    setStep((s) => Math.min(STEP_COUNT, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!form.name || !form.phone || !form.email) {
      toast.error('Please enter your name, phone and email so we can send your quote.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      // Build items description for backend (works for move, office, clearance item sets)
      const allItemDefs = [...MOVE_ITEMS, ...OFFICE_ITEMS, ...CLEARANCE_ITEMS];
      const itemsList = Object.entries(form.items || {})
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => {
          const label = allItemDefs.find((x) => x.id === id)?.label || id;
          return `${qty} × ${label}`;
        });
      const combinedHeavy = [...itemsList, ...form.heavy_items];
      if (form.waste_location) {
        combinedHeavy.unshift(`Waste location: ${form.waste_location}`);
      }
      if (form.van_crew && form.van_crew !== 'ai_recommend') {
        combinedHeavy.unshift(`Customer preference: ${VAN_CREW_LABEL[form.van_crew] || form.van_crew}`);
      }

      const totalBoxes = (parseInt(form.boxes_small, 10) || 0) + (parseInt(form.boxes_medium, 10) || 0) + (parseInt(form.boxes_large, 10) || 0);
      const payload = {
        ...form,
        boxes: totalBoxes,
        load_units: Math.round(computeVanFill(form)),
        heavy_items: combinedHeavy,
        van_crew_preference: form.van_crew && form.van_crew !== 'ai_recommend' ? VAN_CREW_LABEL[form.van_crew] : null,
        photos: form.photos || [],
        base_url: process.env.REACT_APP_BACKEND_URL || '',
        distance_miles: form.distance_miles ? parseFloat(form.distance_miles) : null,
        date: form.date ? toIsoDate(form.date) : null,
      };
      delete payload.items;
      delete payload.waste_location;
      delete payload.van_crew;

      const { data } = await axios.post(`${API}/estimate`, payload, { timeout: 60000 });
      setEstimate(data);
      trackQuoteConversion(data).catch(() => {});
      const list = JSON.parse(localStorage.getItem('mv_estimates') || '[]');
      const slim = { ...payload };
      delete slim.photos; // keep localStorage small
      list.push({ ...slim, result: data, createdAt: new Date().toISOString() });
      localStorage.setItem('mv_estimates', JSON.stringify(list));
      setStep(STEP_COUNT);
    } catch (e) {
      console.error(e);
      toast.error('We could not generate a quote right now. Please call us on 07985 291097.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setEstimate(null);
    setStep(0);
    setForm(INITIAL_FORM);
  };

  const progress = Math.round((step / STEP_COUNT) * 100);

  return (
    <div ref={wizardRef} className="bg-white rounded-xl p-5 sm:p-7 border border-slate-200 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.35)] w-full max-w-xl md:max-w-3xl text-[color:var(--mv-navy)]">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 text-[color:var(--mv-orange)] rounded-full text-xs font-bold tracking-wider uppercase">
          <Sparkles className="w-3.5 h-3.5" /> Instant Quote
        </div>
        {!estimate && (
          <div className="text-xs text-slate-500 font-semibold">Step {Math.min(step + 1, STEP_COUNT)} / {STEP_COUNT}</div>
        )}
      </div>

      {!estimate && (
        <>
          <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[color:var(--mv-navy)]">Get Your Free Quote</h3>
          <p className="text-slate-500 text-sm mt-1">Answer a few quick questions and we’ll price your job instantly.</p>

          <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-[color:var(--mv-orange)] transition-all duration-500"
              style={{ width: `${Math.max(progress, 8)}%` }}
            />
          </div>

          <div className="mt-6 min-h-[280px]">
            {step === 0 && <StepService form={form} update={update} />}
            {step === 1 && <StepSize form={form} update={update} updateItem={updateItem} toggleBulky={toggleBulky} />}
            {step === 2 && <StepAccess form={form} update={update} />}
            {step === 3 && <StepVanCrew form={form} update={update} />}
            {step === 4 && <StepWhereWhen form={form} update={update} />}
            {step === 5 && <StepContact form={form} update={update} />}
          </div>

          <div className="mt-5 flex items-center gap-3">
            {step > 0 && (
              <button type="button" onClick={prev} className="btn-ghost-dark" disabled={loading} data-testid="wizard-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {step < STEP_COUNT - 1 && (
              <button type="button" onClick={next} className="btn-primary ml-auto" data-testid="wizard-continue-btn">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === STEP_COUNT - 1 && (
              <button type="button" onClick={submit} disabled={loading} className="btn-primary ml-auto disabled:opacity-60" data-testid="wizard-submit-btn">
                {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</>) : (<><Sparkles className="w-4 h-4" /> Get My Price</>)}
              </button>
            )}
          </div>

          <p className="text-xs text-slate-400 text-center mt-4">Takes under 60 seconds. No obligation.</p>
        </>
      )}

      {estimate && estimate.available === false && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="quote-unavailable">
          <p className="text-[color:var(--mv-navy)] font-bold">{estimate.unavailable_message}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <a href="tel:07985291097" className="h-11 px-5 rounded-md bg-[color:var(--mv-orange)] text-white font-bold inline-flex items-center" data-testid="quote-unavailable-call">Call 07985 291097</a>
            <button type="button" onClick={reset} className="h-11 px-5 rounded-md border border-slate-200 font-bold" data-testid="quote-unavailable-reset">Start again</button>
          </div>
        </div>
      )}
      {estimate && estimate.available !== false && <EstimateResult estimate={estimate} form={form} onReset={reset} />}
    </div>
  );
};

/* ---------------- Steps ---------------- */

const StepService = ({ form, update }) => (
  <div>
    <StepLabel icon={Package} title="What do you need help with?" />
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
      {SERVICES.map((s) => {
        const Icon = s.icon;
        const active = form.service === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => update({ service: s.id })}
            data-testid={`service-option-${s.id.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            className={`text-left p-3 rounded-lg border transition-all ${active ? 'border-[color:var(--mv-orange)] bg-orange-50 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
          >
            <Icon className={`w-5 h-5 mb-1.5 ${active ? 'text-[color:var(--mv-orange)]' : 'text-[color:var(--mv-navy)]'}`} />
            <div className={`text-sm font-bold ${active ? 'text-[color:var(--mv-orange)]' : 'text-[color:var(--mv-navy)]'}`}>{s.id}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.hint}</div>
          </button>
        );
      })}
    </div>
  </div>
);

// Approximate "van-fill" volume units per item. Anchors (set by Move Van Ltd):
//   • 110 bin bags  ≈ full XLWB         • 160 units ≈ full Luton
//   • 100 broken wood-furniture pieces  ≈ full XLWB  (so each piece ≈ 1 unit)
// These are rough visual guides, not pricing inputs.

const VanFillBar = ({ units }) => {
  const pct = Math.min(100, Math.round((units / CAP_XLWB) * 100));
  let label = 'Empty';
  let tier = '—';
  if (units <= 0) {
    label = 'Add items to see the fit';
  } else if (units < CAP_XLWB * 0.25) {
    label = '≈ 1/4 van';
    tier = '1 Man & Van';
  } else if (units < CAP_XLWB * 0.5) {
    label = '≈ 1/2 van';
    tier = '2 Man & Van';
  } else if (units < CAP_XLWB * 0.75) {
    label = '≈ 3/4 van';
    tier = '2 Man + Medium Van';
  } else if (units <= CAP_XLWB) {
    label = '≈ Full XLWB';
    tier = '2 Man + XLWB';
  } else if (units <= CAP_LUTON) {
    label = '≈ Full Luton';
    tier = '2–3 Man + Luton';
  } else if (units <= CAP_LUTON_PLUS) {
    label = 'More than one Luton';
    tier = '3 Man + Luton or Two Vans';
  } else if (units <= CAP_7_5T) {
    label = '7.5t Lorry territory';
    tier = '7.5 Tonne Lorry (3 Man)';
  } else {
    label = 'Multiple trips / big move';
    tier = '7.5t Lorry + extra van';
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="van-fill-bar">
      <div className="flex items-center justify-between text-xs">
        <div className="font-bold text-[color:var(--mv-navy)]">Estimated van fill</div>
        <div className="font-semibold text-[color:var(--mv-orange)]">{label}</div>
      </div>
      <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${Math.max(pct, units > 0 ? 5 : 0)}%`,
            background: units >= CAP_LUTON
              ? 'linear-gradient(90deg, #ff6b00 0%, #e11d48 100%)'
              : 'linear-gradient(90deg, #ff8c38 0%, #ff6b00 100%)',
          }}
        />
      </div>
      {units > 0 && (
        <div className="mt-1 text-[11px] text-slate-500">
          Suggested fit: <span className="font-semibold text-[color:var(--mv-navy)]">{tier}</span>
        </div>
      )}
    </div>
  );
};

const PhotoAutofill = ({ form, update }) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [hashes, setHashes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const hashFile = async (f) => {
    try {
      const buf = await f.arrayBuffer();
      const digest = await crypto.subtle.digest('SHA-256', buf);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return `${f.size}-${f.lastModified}-${f.name}`; // fallback fingerprint
    }
  };

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 8 - files.length);
    const isImage = (f) => /^image\//.test(f.type || '') || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name || '');
    const okFormat = picked.filter(isImage);
    if (okFormat.length !== picked.length) {
      toast.error('Only photos are supported (JPEG, PNG, WebP or iPhone HEIC).');
    }

    // Deduplicate using SHA-256 of the file bytes
    const newHashes = await Promise.all(okFormat.map(hashFile));
    const unique = [];
    const uniqueHashes = [];
    let duplicates = 0;
    okFormat.forEach((f, i) => {
      const h = newHashes[i];
      if (hashes.includes(h) || uniqueHashes.includes(h)) {
        duplicates += 1;
      } else {
        unique.push(f);
        uniqueHashes.push(h);
      }
    });
    if (duplicates > 0) {
      toast.error(`Skipped ${duplicates} duplicate photo${duplicates > 1 ? 's' : ''}.`);
    }

    const next = [...files, ...unique].slice(0, 8);
    const nextHashes = [...hashes, ...uniqueHashes].slice(0, 8);
    setFiles(next);
    setHashes(nextHashes);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
    if (galleryRef.current) galleryRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  const remove = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    setHashes(hashes.filter((_, i) => i !== idx));
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const fileToBase64 = (f) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = reject;
      r.readAsDataURL(f);
    });

  const analyze = async () => {
    if (files.length === 0) {
      toast.error('Please add at least one photo.');
      return;
    }
    setLoading(true);
    setNotes('');
    try {
      const images = await Promise.all(files.map(fileToBase64));
      const { data } = await axios.post(
        `${API}/estimate/analyze-photos`,
        { images, service: form.service, property_size: form.property_size || '' },
        { timeout: 90000 }
      );
      const detected = data?.items || {};
      const mergedItems = { ...(form.items || {}) };
      Object.entries(detected).forEach(([id, qty]) => {
        if (Number.isFinite(Number(qty)) && Number(qty) > 0) {
          mergedItems[id] = Math.max(mergedItems[id] || 0, Number(qty));
        }
      });
      // Store the uploaded photos (base64) on the form so they can be attached
      // to the lead email on submit.
      update({ items: mergedItems, photos: images });
      setNotes(data?.notes || 'We pre-filled the items based on your photos. Please review and adjust the counts below.');
      toast.success('Photos analysed — items pre-filled below.');
    } catch (e) {
      console.error(e);
      toast.error('We could not analyse the photos. Please fill in the items manually.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-dashed border-[color:var(--mv-orange)]/60 bg-orange-50/40 p-4">
      <div className="flex items-start gap-2">
        <Camera className="w-4 h-4 text-[color:var(--mv-orange)] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-extrabold text-[color:var(--mv-navy)]">Snap a few photos — we’ll pre-fill the items</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Add up to 8 clear photos of the rubbish/clearance. We’ll estimate the volume and tick the right items for you to review. Optional — you can skip and fill in manually.
          </div>
          <div className="text-[11px] text-[color:var(--mv-orange)] font-semibold mt-1">
            Tip: shoot from ~2m back so the whole pile is in frame, then add a close-up with a familiar object for scale (bin bag, foot, broom).
          </div>
        </div>
      </div>

      <input
        ref={galleryRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        onChange={onPick}
        data-testid="photo-gallery-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
        data-testid="photo-camera-input"
      />

      {previews.length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {previews.map((src, i) => (
            <div key={src} className="relative group">
              <img src={src} alt={`waste ${i + 1}`} className="w-full h-20 object-cover rounded-md border border-slate-200" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
              <div className="hidden w-full h-20 rounded-md border border-slate-200 bg-slate-100 text-slate-500 text-[10px] font-bold items-center justify-center text-center px-1" data-testid={`photo-heic-placeholder-${i}`}>iPhone photo {i + 1} ✓</div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900/80 text-white text-[10px] font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove photo"
                data-testid={`photo-remove-${i}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => galleryRef.current?.click()}
          disabled={files.length >= 8 || loading}
          className="btn-ghost-dark text-xs h-9 disabled:opacity-50"
          data-testid="photo-pick-gallery-btn"
        >
          <ImageIcon className="w-3.5 h-3.5" /> {files.length === 0 ? 'Choose from gallery' : `Gallery (${files.length}/8)`}
        </button>
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={files.length >= 8 || loading}
          className="btn-ghost-dark text-xs h-9 disabled:opacity-50"
          data-testid="photo-pick-camera-btn"
        >
          <Camera className="w-3.5 h-3.5" /> Take photo
        </button>
        <button
          type="button"
          onClick={analyze}
          disabled={files.length === 0 || loading}
          className="btn-primary text-xs h-9 disabled:opacity-50"
          data-testid="photo-analyze-btn"
        >
          {loading ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing…</>) : (<><Sparkles className="w-3.5 h-3.5" /> Analyse & pre-fill</>)}
        </button>
      </div>

      {notes && (
        <div className="mt-3 text-xs text-slate-600 bg-white/70 rounded-md p-2 border border-slate-200">
          {notes}
        </div>
      )}
    </div>
  );
};

const StepSize = ({ form, update, updateItem, toggleBulky }) => {
  const clearance = isClearanceSvc(form.service);
  const office = isOfficeSvc(form.service);
  const sizes = clearance ? SIZES_CLEARANCE : SIZES_MOVE;
  const items = clearance ? CLEARANCE_ITEMS : (office ? OFFICE_ITEMS : MOVE_ITEMS);
  const [itemQuery, setItemQuery] = useState('');
  const q = itemQuery.trim().toLowerCase();
  const shownItems = q ? items.filter(it => `${it.label} ${it.hint || ''} ${it.group || ''}`.toLowerCase().includes(q)) : items;
  const bulky = clearance ? BULKY_CLEARANCE : BULKY;
  const sizeTitle = clearance ? 'How much is there (roughly)?' : 'Property size';
  const itemsTitle = clearance ? 'What kind of waste? (tap +/−)' : office ? 'Office items (tap +/−)' : 'Items to move (tap +/−)';

  return (
    <div className="space-y-5">
      {clearance && <PhotoAutofill form={form} update={update} />}
      <div>
        <StepLabel icon={clearance ? Trash2 : HomeIcon} title={sizeTitle} />
        <div className="flex flex-wrap gap-2">
          {sizes.map((s) => {
            const active = form.property_size === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => update({ property_size: s })}
                className={`px-3.5 py-2 rounded-full border text-sm font-semibold transition-colors ${active ? 'bg-[color:var(--mv-orange)] text-white border-[color:var(--mv-orange)]' : 'bg-white text-[color:var(--mv-navy)] border-slate-200 hover:border-slate-300'}`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
      <VanFillBar units={computeVanFill(form)} />

      <div>
        <StepLabel icon={Package} title={itemsTitle} small />
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          {items.length > 15 && (
            <div className="p-2 border-b border-slate-100 bg-slate-50">
              <input value={itemQuery} onChange={(e) => setItemQuery(e.target.value)} placeholder="Search items (e.g. microwave)" className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="items-search" />
            </div>
          )}
          <div className="grid grid-cols-1 max-h-[460px] overflow-y-auto" data-testid="items-picker">
            {shownItems.length === 0 && <div className="p-3 text-sm text-slate-500" data-testid="items-no-match">No match. Add it in the notes box and we'll include it.</div>}
            {shownItems.map((it, idx) => {
              const Icon = it.icon;
              const qty = form.items?.[it.id] || 0;
              const header = it.group && it.group !== shownItems[idx - 1]?.group;
              return (
                <React.Fragment key={it.id}>
                {header && <div className="sticky top-0 z-[1] px-3 py-1.5 bg-slate-100 text-[11px] font-black uppercase tracking-wider text-slate-600" data-testid={`items-group-${it.group}`}>{it.group}</div>}
                <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-100">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Icon className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm sm:text-base font-bold text-[color:var(--mv-navy)] leading-tight">{it.label}</div>
                      {it.hint && <div className="text-xs text-slate-600 leading-snug">{it.hint}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={() => updateItem(it.id, -1)} className="w-10 h-10 rounded-md border-2 border-slate-300 flex items-center justify-center text-[color:var(--mv-navy)] hover:bg-slate-50 active:scale-95 transition-transform disabled:opacity-40" disabled={qty === 0} aria-label={`Decrease ${it.label}`} data-testid={`item-${it.id}-dec`}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="min-w-[2rem] text-center text-lg font-black text-[color:var(--mv-navy)]" data-testid={`item-${it.id}-qty`}>{qty}</span>
                    <button type="button" onClick={() => updateItem(it.id, 1)} className="w-10 h-10 rounded-md border-2 border-[color:var(--mv-orange)] bg-orange-50 flex items-center justify-center text-[color:var(--mv-orange)] hover:bg-orange-100 active:scale-95 transition-transform" aria-label={`Increase ${it.label}`} data-testid={`item-${it.id}-inc`}>
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {!clearance && (
        <div>
          <StepLabel icon={Boxes} title="How many boxes?" small />
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100" data-testid="boxes-picker">
              {[
                { id: 'boxes_small', label: 'Small boxes', hint: 'archive / shoebox' },
                { id: 'boxes_medium', label: 'Medium boxes', hint: 'standard removal box' },
                { id: 'boxes_large', label: 'Large boxes', hint: 'big removal / wardrobe box' },
              ].map((b) => {
                const qty = form[b.id] || 0;
                return (
                <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-3" data-testid={`${b.id}-row`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Boxes className="w-5 h-5 text-[color:var(--mv-orange)] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm sm:text-base font-bold text-[color:var(--mv-navy)] leading-tight">{b.label}</div>
                      <div className="text-xs text-slate-600 leading-snug">{b.hint}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => update({ [b.id]: Math.max(0, qty - 1) })}
                      className="w-10 h-10 rounded-md border-2 border-slate-300 flex items-center justify-center text-[color:var(--mv-navy)] hover:bg-slate-50 active:scale-95 transition-transform disabled:opacity-40"
                      disabled={qty === 0}
                      aria-label={`Decrease ${b.label}`}
                      data-testid={`${b.id}-dec`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="min-w-[2.5rem] text-center text-lg font-black text-[color:var(--mv-navy)]" aria-live="polite" data-testid={`${b.id}-qty`}>{qty}</span>
                    <button
                      type="button"
                      onClick={() => update({ [b.id]: qty + 1 })}
                      className="w-10 h-10 rounded-md border-2 border-[color:var(--mv-orange)] bg-orange-50 flex items-center justify-center text-[color:var(--mv-orange)] hover:bg-orange-100 active:scale-95 transition-transform"
                      aria-label={`Increase ${b.label}`}
                      data-testid={`${b.id}-inc`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-3 py-2 bg-slate-50 text-sm text-[color:var(--mv-navy)] flex justify-between" data-testid="boxes-total">
            <span>Total boxes</span><strong>{(form.boxes_small || 0) + (form.boxes_medium || 0) + (form.boxes_large || 0)}</strong>
          </div>
        </div>
        </div>
      )}

      <div>
        <StepLabel icon={Package} title={clearance ? 'Any special / hazardous items?' : 'Any special / extra-bulky items?'} small />
        <div className="flex flex-wrap gap-2">
          {bulky.map((h) => {
            const active = form.heavy_items.includes(h);
            return (
              <button
                key={h}
                type="button"
                onClick={() => toggleBulky(h)}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${active ? 'bg-[color:var(--mv-navy)] text-white border-[color:var(--mv-navy)]' : 'bg-white text-[color:var(--mv-navy)] border-slate-200 hover:border-slate-300'}`}
              >
                {active ? '✓ ' : '+ '}{h}
              </button>
            );
          })}
        </div>
      </div>

      {clearance && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-3 flex gap-2 items-start text-xs text-slate-700">
          <Camera className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-[color:var(--mv-navy)]">Please send us photos on WhatsApp</div>
            <div className="mt-1 leading-relaxed">For loose rubbish — bin bags, cardboard, garden waste, piles of wood — a few quick photos help us confirm the exact volume and give you a firm final price. The more detail the better.</div>
          </div>
        </div>
      )}
    </div>
  );
};

const StepAccess = ({ form, update }) => {
  const clearance = isClearanceSvc(form.service);
  return (
    <div className="space-y-5">
      {clearance ? (
        <>
          <div>
            <StepLabel icon={Trash2} title="Where is the rubbish?" />
            <div className="flex flex-wrap gap-2">
              {WASTE_LOCATIONS.map((w) => {
                const active = form.waste_location === w;
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => update({ waste_location: w })}
                    className={`px-3.5 py-2 rounded-full border text-sm font-semibold transition-colors ${active ? 'bg-[color:var(--mv-orange)] text-white border-[color:var(--mv-orange)]' : 'bg-white text-[color:var(--mv-navy)] border-slate-200 hover:border-slate-300'}`}
                  >
                    {w}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <StepLabel icon={MapPin} title="Access to the rubbish" />
            <AccessPills value={form.pickup_access} onChange={(v) => update({ pickup_access: v })} />
            <p className="text-xs text-slate-500 mt-2">Tip: let us know in the notes if there's a long carry, narrow stairs or tricky parking.</p>
          </div>
        </>
      ) : (
        <>
          <div>
            <StepLabel icon={MapPin} title="Access at pickup" />
            <AccessPills value={form.pickup_access} onChange={(v) => update({ pickup_access: v })} />
          </div>
          <div>
            <StepLabel icon={MapPin} title="Access at delivery" />
            <AccessPills value={form.dropoff_access} onChange={(v) => update({ dropoff_access: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Toggle active={form.packing} onClick={() => update({ packing: !form.packing })} title="Packing needed" hint="We bring boxes & pack" />
            <Toggle active={form.dismantle} onClick={() => update({ dismantle: !form.dismantle })} title="Dismantle furniture" hint="Beds, wardrobes, etc." />
          </div>
        </>
      )}
    </div>
  );
};

const StepVanCrew = ({ form, update }) => (
  <div className="space-y-4">
    <StepLabel icon={Truck} title="Which van & crew would you like?" />
    <p className="text-sm text-slate-500 -mt-2">
      Pick a package or let us recommend the best fit. We’ll fine-tune the final quote based on the job details you’ve just entered.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {VAN_CREW_OPTIONS.map((opt) => {
        const active = form.van_crew === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            data-testid={`van-crew-${opt.id}`}
            onClick={() => update({ van_crew: opt.id })}
            className={`text-left rounded-xl border p-3 transition-all ${
              active
                ? 'border-[color:var(--mv-orange)] bg-orange-50/60 shadow-[0_8px_20px_-10px_rgba(255,107,0,0.6)]'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-[color:var(--mv-navy)] text-sm leading-snug">{opt.title}</div>
              {opt.badge && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[color:var(--mv-orange)] text-white">
                  {opt.badge}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">{opt.subtitle}</div>
          </button>
        );
      })}
    </div>
    <p className="text-xs text-slate-400">
      Not sure? Pick the first option and we’ll recommend the right van & crew based on your job.
    </p>
  </div>
);

const StepWhereWhen = ({ form, update }) => {
  const clearance = isClearanceSvc(form.service);
  const [fromState, setFromState] = useState({ status: 'idle', lat: null, lng: null, formatted: '' });
  const [toState, setToState] = useState({ status: 'idle', lat: null, lng: null, formatted: '' });

  // Validate a UK postcode via postcodes.io (free, no auth)
  const validatePostcode = async (pc, setter) => {
    const clean = pc.replace(/\s/g, '');
    if (clean.length < 5) {
      setter({ status: 'idle', lat: null, lng: null, formatted: '' });
      return;
    }
    setter({ status: 'checking', lat: null, lng: null, formatted: '' });
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(clean)}`);
      if (!res.ok) {
        setter({ status: 'invalid', lat: null, lng: null, formatted: '' });
        return;
      }
      const data = await res.json();
      const r = data.result;
      setter({ status: 'valid', lat: r.latitude, lng: r.longitude, formatted: r.postcode });
    } catch {
      setter({ status: 'invalid', lat: null, lng: null, formatted: '' });
    }
  };

  // Debounced validation
  useEffect(() => {
    const t = setTimeout(() => validatePostcode(form.from_postcode, setFromState), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.from_postcode]);
  useEffect(() => {
    const t = setTimeout(() => validatePostcode(form.to_postcode, setToState), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.to_postcode]);

  // Road distance from the backend (same routing as the quote rules and calendar)
  const [distance, setDistance] = useState(null);
  useEffect(() => {
    if (fromState.status !== 'valid' || toState.status !== 'valid') { setDistance(null); return; }
    let live = true;
    axios.get(`${API}/road-distance`, { params: { from_pc: fromState.formatted, to_pc: toState.formatted } })
      .then(({ data }) => { if (live) setDistance(data.miles != null ? Math.max(0.1, data.miles) : null); })
      .catch(() => { if (live) setDistance(null); });
    return () => { live = false; };
  }, [fromState, toState]);

  // Push the computed distance up to the form
  useEffect(() => {
    update({ distance_miles: distance ? String(distance) : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distance]);

  return (
    <div className="space-y-4">
      <StepLabel icon={MapPin} title={clearance ? 'Collection address' : 'Where are we moving between?'} />
      <div className={`grid grid-cols-1 ${clearance ? '' : 'sm:grid-cols-2'} gap-3`}>
        <PostcodeField
          placeholder={clearance ? 'Collection postcode (e.g. EC2A 4NA)' : 'From postcode (e.g. EC2A 4NA)'}
          value={form.from_postcode}
          onChange={(v) => update({ from_postcode: v })}
          state={fromState}
        />
        {!clearance && (
          <PostcodeField
            placeholder="To postcode (e.g. SW1A 1AA)"
            value={form.to_postcode}
            onChange={(v) => update({ to_postcode: v })}
            state={toState}
          />
        )}
      </div>

      {(fromState.status === 'valid' || (!clearance && toState.status === 'valid')) && (
        <div className="grid grid-cols-1 gap-3">
          <div>{fromState.status === 'valid' && (<>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{clearance ? 'Collection address' : 'Moving from'}</div>
            <AddressFinder postcode={form.from_postcode} house={form.from_house_number} address={form.from_address} onChange={({ house, address }) => update({ from_house_number: house, from_address: address })} testPrefix="wiz-from" />
          </>)}</div>
          {!clearance && <div>{toState.status === 'valid' && (<>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Moving to</div>
            <AddressFinder postcode={form.to_postcode} house={form.to_house_number} address={form.to_address} onChange={({ house, address }) => update({ to_house_number: house, to_address: address })} testPrefix="wiz-to" />
          </>)}</div>}
        </div>
      )}

      {!clearance && distance !== null && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex gap-2 items-center text-sm">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span className="text-slate-700">
            Both postcodes verified. Estimated driving distance: <span className="font-bold text-[color:var(--mv-navy)]">{distance} miles</span>
          </span>
        </div>
      )}
      {clearance && fromState.status === 'valid' && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex gap-2 items-center text-sm">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span className="text-slate-700">
            Collection postcode verified: <span className="font-bold text-[color:var(--mv-navy)]">{fromState.formatted}</span>
          </span>
        </div>
      )}
      {(fromState.status === 'invalid' || (!clearance && toState.status === 'invalid')) && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex gap-2 items-center text-sm">
          <Info className="w-4 h-4 text-red-600 shrink-0" />
          <span className="text-slate-700">Please enter a valid UK postcode.</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="h-11 w-full rounded-md border border-slate-200 bg-white text-left pl-9 pr-3 text-sm text-[color:var(--mv-navy)] relative hover:border-slate-300" data-testid="preferred-date-btn">
              <CalIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              {form.date ? format(form.date, 'PPP') : <span className="text-slate-400">Preferred date</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <ShadCalendar
              mode="single"
              selected={form.date}
              onSelect={(d) => update({ date: d, slot_checked: false })}
              disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <div className="relative">
          <CalIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <select
            data-testid="preferred-time-select"
            value={form.time || ''}
            onChange={(e) => update({ time: e.target.value, slot_checked: false })}
            className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white pl-9 pr-9 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)] focus:border-transparent"
          >
            <option value="">Preferred time slot…</option>
            <optgroup label="Early morning">
              {['06:00','06:30','07:00','07:30'].map(t => <option key={t} value={t}>{t}</option>)}
            </optgroup>
            <optgroup label="Morning">
              {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30'].map(t => <option key={t} value={t}>{t}</option>)}
            </optgroup>
            <optgroup label="Afternoon">
              {['12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30'].map(t => <option key={t} value={t}>{t}</option>)}
            </optgroup>
            <optgroup label="Evening">
              {['17:00','17:30','18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30','22:00','22:30','23:00'].map(t => <option key={t} value={t}>{t}</option>)}
            </optgroup>
            <option value="Flexible">I’m flexible — pick the best time</option>
          </select>
          <svg aria-hidden="true" className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z" clipRule="evenodd"/></svg>
        </div>
      </div>

      <SlotAvailability form={form} update={update} />
      <textarea
        className="w-full rounded-md border border-slate-200 bg-white px-3 py-3 text-sm text-[color:var(--mv-navy)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)] resize-none"
        rows={3}
        placeholder={clearance
          ? 'Please describe the waste in detail (bagged, loose, bulky items, where it is). Photos help us a lot — send them on WhatsApp after you get your quote.'
          : 'Anything else we should know? (parking, timing, fragile items…)'}
        value={form.notes}
        onChange={(e) => update({ notes: e.target.value })}
      />
    </div>
  );
};

const PostcodeField = ({ placeholder, value, onChange, state }) => {
  const borderClass =
    state.status === 'valid' ? 'border-green-400 focus:ring-green-500' :
    state.status === 'invalid' ? 'border-red-300 focus:ring-red-400' :
    'border-slate-200 focus:ring-[color:var(--mv-orange)]';
  return (
    <div className="relative">
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className={`h-11 w-full rounded-md border bg-white px-3 pr-10 text-sm text-[color:var(--mv-navy)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${borderClass}`}
        autoComplete="postal-code"
        maxLength={10}
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {state.status === 'checking' && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
        {state.status === 'valid' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
        {state.status === 'invalid' && <Info className="w-4 h-4 text-red-500" />}
      </div>
    </div>
  );
};

/** Live availability check against drivers, vans and crew (no internal details shown). */
const SlotAvailability = ({ form, update }) => {
  const [checking, setChecking] = useState(false);
  const ready = !!form.date && !!form.time;
  const confirmed = !!form.slot_checked;
  const slotSameDay = form.date && toIsoDate(form.date) === toIsoDate(new Date());

  const run = async () => {
    if (!ready || checking) return;
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/estimate/check-slot`, { date: toIsoDate(form.date), time: form.time });
      if (data.available) update({ slot_checked: true });
      else toast.error(data.message);
    } catch {
      toast.error('Could not check availability right now — please try again.');
    } finally { setChecking(false); }
  };

  if (!ready && !confirmed) {
    return (
      <div className="rounded-lg bg-slate-50 border border-dashed border-slate-200 p-3 text-xs text-slate-500 flex items-center gap-2">
        <CalIcon className="w-4 h-4 text-slate-400 shrink-0" />
        Pick a date and time slot, then check availability.
      </div>
    );
  }

  if (confirmed) {
    const dateText = form.date ? format(form.date, 'EEE, d MMM yyyy') : '';
    return (
      <div className="rounded-lg bg-green-50 border border-green-200 p-3" data-testid="slot-available-banner">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-bold text-[color:var(--mv-navy)]">We have a booking slot available!</div>
            <div className="text-slate-600 text-xs mt-0.5">
              {dateText} · <span className="font-semibold">{form.time}</span>
              {form.time !== 'Flexible' && ' crew arrival'} — provisionally held while you finish your quote.
            </div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-green-200 flex items-start gap-2 text-xs">
          <Truck className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
          <div className="text-slate-600">
            <span className="font-bold text-[color:var(--mv-navy)]">A van &amp; crew team are free in this window</span>
            {' '}— we'll match you to the right one once you finish your quote.
          </div>
        </div>
        {slotSameDay && (
          <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
            <strong>Same-day heads-up:</strong> same-day jobs carry a <strong>£35 surcharge</strong> and we offer a wider arrival window — <strong>between {form.time} and {addHours(form.time, 6)}</strong>. Please <strong>WhatsApp us right after booking</strong> so we don't miss anything urgent.
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={run}
      disabled={checking}
      data-testid="check-availability-btn"
      className="w-full h-11 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] disabled:opacity-60 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
    >
      {checking ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" /> Checking live calendar…
        </>
      ) : (
        <>
          <CalIcon className="w-4 h-4" /> Check availability for this slot
        </>
      )}
    </button>
  );
};

const StepContact = ({ form, update }) => {
  const emailValid = /\S+@\S+\.\S+/.test(form.email || '');
  return (
    <div className="space-y-3">
      <StepLabel icon={User} title="Where should we send your quote?" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <IconField icon={User} placeholder="Your name" value={form.name} onChange={(e) => update({ name: e.target.value })} autoComplete="name" />
        <IconField icon={Phone} placeholder="Phone number" value={form.phone} onChange={(e) => update({ phone: e.target.value })} type="tel" autoComplete="tel" inputMode="tel" />
      </div>
      <IconField icon={Mail} placeholder="Email address (we’ll send you the quote)" value={form.email} onChange={(e) => update({ email: e.target.value })} type="email" autoComplete="email" inputMode="email" data-testid="contact-email" />
      {emailValid && (
        <div className="text-xs text-slate-600 bg-[color:var(--mv-sky)] rounded-md px-3 py-2 flex items-center gap-2" data-testid="email-preview">
          <Mail className="w-3.5 h-3.5 text-[color:var(--mv-orange)]" />
          <span>We’ll send your quote to <strong className="text-[color:var(--mv-navy)]">{form.email}</strong>.</span>
        </div>
      )}
    </div>
  );
};

/* ---------------- Result ---------------- */

const DEPOSIT_PERCENT = 20;
const SAME_DAY_SURCHARGE = 35;

const TIME_SLOT_GROUPS = [
  ['Early morning', ['06:00','06:30','07:00','07:30']],
  ['Morning', ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30']],
  ['Afternoon', ['12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30']],
  ['Evening', ['17:00','17:30','18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30','22:00','22:30','23:00']],
];

const toIsoDate = (d) => {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const isToday = (d) => d && toIsoDate(d) === toIsoDate(new Date());

/** Add N hours to a "HH:MM" time string, wrapping past midnight. */
const addHours = (hhmm, hours) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  if (Number.isNaN(h)) return '';
  const total = (h + hours) * 60 + m;
  const outH = Math.floor((total % (24 * 60) + 24 * 60) % (24 * 60) / 60);
  const outM = total % 60;
  return `${String(outH).padStart(2, '0')}:${String(outM).padStart(2, '0')}`;
};

const EstimateResult = ({ estimate, form, onReset }) => {
  const navigate = useNavigate();
  const price = estimate.price ?? estimate.low_total ?? 0;

  // Booking widget state
  const [bookDate, setBookDate] = useState(null);
  const [bookTime, setBookTime] = useState('');
  const [bookChecked, setBookChecked] = useState(false);
  const [bookChecking, setBookChecking] = useState(false);
  const [booking, setBooking] = useState(null);
  const [weekdaySwap, setWeekdaySwap] = useState(false); // true when customer accepted a weekday swap

  // Deposit state

  // Optional extras (service-aware)
  const isClearance = isClearanceSvc(form.service);
  const EXTRA_PROTECTION = { code: 'protection_plus', label: 'Move Van Protection Plus', price: 45, blurb: 'Enhanced cover for high-value items — up to £5,000 extra protection on the day.' };
  const EXTRA_CLEANUP = { code: 'cleanup_sweep', label: 'Clean sweep after waste collection', price: 15, blurb: 'Our crew does a quick sweep after loading so you hand back a tidy space.' };
  const extraOption = isClearance ? EXTRA_CLEANUP : EXTRA_PROTECTION;
  const [extraOn, setExtraOn] = useState(false);
  const selectedExtras = extraOn ? [{ code: extraOption.code, label: extraOption.label, price: extraOption.price }] : [];
  const extrasTotal = selectedExtras.reduce((a, b) => a + b.price, 0);

  // Pricing math
  const sameDay = isToday(bookDate);
  const dayOfWeek = bookDate ? bookDate.getDay() : null; // 0=Sun, 6=Sat
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const sameDaySurcharge = sameDay ? SAME_DAY_SURCHARGE : 0;
  const subtotal = price + sameDaySurcharge + extrasTotal;
  // 10% weekday saver — only if customer accepted the swap AND it's not same-day
  const weekdaySaver = (weekdaySwap && !sameDay && !isWeekend) ? Math.round(subtotal * 0.10) : 0;
  const finalPrice = subtotal - weekdaySaver;
  const deposit = Math.round((finalPrice * DEPOSIT_PERCENT) / 100);
  const balance = Math.max(finalPrice - deposit, 0);

  // Suggest the nearest weekday to a chosen weekend date
  const suggestedWeekday = useMemo(() => {
    if (!bookDate || !isWeekend) return null;
    const next = new Date(bookDate);
    if (dayOfWeek === 6) { next.setDate(next.getDate() + 2); }  // Sat → Mon
    else if (dayOfWeek === 0) { next.setDate(next.getDate() + 1); } // Sun → Mon
    return next;
  }, [bookDate, dayOfWeek, isWeekend]);

  const whatsappText = useMemo(() => encodeURIComponent(
    `Hi Move Van Ltd — I just got an instant quote on your website.\n\n` +
    `Service: ${form.service}\nProperty: ${form.property_size}\n` +
    `Recommended van: ${estimate.van_recommendation}\n` +
    `Quoted price: £${price} (20% deposit £${deposit})\n\n` +
    `Please confirm for ${form.name} (${form.phone}).`
  ), [form, estimate, price, deposit]);
  const whatsappUrl = `https://wa.me/447985291097?text=${whatsappText}`;

  const sameDayWhatsAppText = useMemo(() => encodeURIComponent(
    `URGENT — SAME-DAY booking just made on the website.\n\n` +
    `Name: ${form.name}\nPhone: ${form.phone}\n` +
    `Route: ${form.from_postcode} → ${form.to_postcode}\n` +
    `Date/Time: ${bookDate ? toIsoDate(bookDate) : 'today'} ${bookTime || ''}\n` +
    `Final total: £${finalPrice} (incl. £${SAME_DAY_SURCHARGE} same-day surcharge).\n\n` +
    `Please confirm van + crew availability now.`
  ), [form, bookDate, bookTime, finalPrice]);
  const sameDayWhatsAppUrl = `https://wa.me/447985291097?text=${sameDayWhatsAppText}`;


  const runAvailabilityCheck = () => {
    if (!bookDate || !bookTime || bookChecking) return;
    setBookChecking(true);
    axios.post(`${API}/estimate/check-slot`, { date: toIsoDate(bookDate), time: bookTime, quote_id: estimate.id })
      .then(({ data }) => (data.available ? setBookChecked(true) : toast.error(data.message)))
      .catch(() => toast.error('Could not check availability right now — please try again.'))
      .finally(() => setBookChecking(false));
  };

  const acceptWeekdaySwap = () => {
    if (!suggestedWeekday) return;
    setBookDate(suggestedWeekday);
    setWeekdaySwap(true);
    setBookChecked(false);
    toast.success(`Great choice — swapped to ${format(suggestedWeekday, 'EEE, d MMM')} with a 10% weekday saver.`);
  };

  if (estimate.manual_quote) {
    return (
      <div className="rounded-2xl border-2 border-[color:var(--mv-navy)] bg-white p-5" data-testid="manual-quote">
        <div className="text-[11px] font-black tracking-[0.22em] uppercase text-[color:var(--mv-orange)]">Price to be confirmed</div>
        <h3 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)] mt-1">We'll check this one by hand</h3>
        <p className="text-sm text-slate-600 mt-2">{estimate.summary}</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <a href="tel:07985291097" className="btn-primary justify-center" data-testid="manual-quote-call"><Phone className="w-4 h-4" /> Call 07985 291097</a>
          <a href={`https://wa.me/447985291097?text=${encodeURIComponent(`Hi Move Van Ltd — my ${form.service || ''} job needs a price check (quote #${(estimate.id || '').slice(0, 6).toUpperCase()}). ${form.name || ''}`)}`} target="_blank" rel="noreferrer" className="btn-ghost-dark justify-center" data-testid="manual-quote-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
        </div>
        <button onClick={onReset} className="w-full mt-3 text-xs text-slate-500 font-semibold uppercase tracking-wider" data-testid="manual-quote-reset">Start a new quote</button>
      </div>
    );
  }

  return (
    <div>
      <div className="rounded-2xl bg-gradient-to-br from-[color:var(--mv-navy)] to-[#13315c] text-white p-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase">
          <CheckCircle2 className="w-3.5 h-3.5 text-green-300" /> Your Instant Quote
        </div>
        <div className="mt-4 font-display text-5xl sm:text-6xl font-extrabold leading-none">
          £{price.toLocaleString()}
        </div>
        <div className="mt-2 text-sm text-white/80">
          <span className="font-bold text-[color:var(--mv-orange-2)]">{estimate.van_recommendation}</span>
          {' · '}
          <span>{estimate.hours_estimate}</span>
        </div>
      </div>

      {estimate.summary && (
        <p className="mt-4 text-sm text-slate-700 bg-[color:var(--mv-sky)] rounded-lg p-4 leading-relaxed">
          {estimate.summary}
        </p>
      )}

      <div className="mt-4 rounded-xl bg-[color:var(--mv-orange)] text-white p-4 text-center shadow-[0_12px_24px_-12px_rgba(255,107,0,0.6)]">
        <div className="text-[11px] font-black tracking-[0.22em] uppercase">Discounts available</div>
        <div className="font-display text-lg sm:text-xl font-extrabold mt-1 leading-tight">
          Call and book now to save 10% on your booking
        </div>
        <a href="tel:07985291097" onClick={() => countOfferTap('quote-result')} className="mt-2 inline-flex items-center gap-2 rounded-full bg-white text-[color:var(--mv-orange)] font-bold px-4 h-10" data-testid="result-call-save-btn"><Phone className="w-4 h-4" /> Call to book & save 10%</a>
        <div className="text-xs text-white/90 mt-2">Mention this offer when you call. Your discount will be included in the price agreed before you book.</div>
      </div>

      {/* Step 1 — Pick date & time */}
      <div className={`mt-6 rounded-2xl border-2 ${booking ? 'border-emerald-400' : 'border-[color:var(--mv-navy)]'} bg-white overflow-hidden`}>
        <div className="bg-[color:var(--mv-navy)] text-white px-5 py-3 flex items-center justify-between">
          <div className="text-[11px] font-black tracking-[0.22em] uppercase text-[color:var(--mv-orange-2)]">
            Step 1 · Pick your date & time
          </div>
          <div className="text-[11px] font-bold tracking-wider uppercase text-white/70">
            {booking ? '✔ Booked' : bookChecked ? 'Slot found' : 'Required'}
          </div>
        </div>

        {booking ? (
          <div className="p-5">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="booking-confirmed">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-bold text-[color:var(--mv-navy)]">Booking confirmed · {booking.date} · {booking.time}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {estimate.van_recommendation} · {estimate.hours_estimate} · Final total <strong className="text-[color:var(--mv-navy)]">£{booking.final_price}</strong>
                    {booking.same_day ? <> (incl. <strong>+£{SAME_DAY_SURCHARGE} same-day surcharge</strong>)</> : null}
                  </div>
                </div>
              </div>
              {booking.same_day && (
                <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
                  <div className="font-bold">Please WhatsApp us <u>right now</u> so we don't miss your same-day job.</div>
                  <a href={sameDayWhatsAppUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebc5a] text-white text-sm font-bold px-4 py-2 rounded-md" data-testid="sameday-whatsapp-btn">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Move Van Ltd now
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="book-date-btn" className="h-11 w-full rounded-md border border-slate-200 bg-white text-left pl-9 pr-3 text-sm text-[color:var(--mv-navy)] relative hover:border-slate-300">
                    <CalIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    {bookDate ? format(bookDate, 'PPP') : <span className="text-slate-400">Choose booking date</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <ShadCalendar
                    mode="single"
                    selected={bookDate}
                    onSelect={(d) => { setBookDate(d); setBookChecked(false); }}
                    disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <div className="relative">
                <CalIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <select
                  data-testid="book-time-select"
                  value={bookTime}
                  onChange={(e) => { setBookTime(e.target.value); setBookChecked(false); }}
                  className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white pl-9 pr-9 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
                >
                  <option value="">Pick a time slot…</option>
                  {TIME_SLOT_GROUPS.map(([label, items]) => (
                    <optgroup key={label} label={label}>
                      {items.map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                  ))}
                </select>
                <svg aria-hidden="true" className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z" clipRule="evenodd"/></svg>
              </div>
            </div>

            {sameDay && (
              <div className="rounded-lg bg-red-50 border-2 border-red-200 p-3 text-sm text-red-900" data-testid="sameday-notice">
                <div className="font-black uppercase tracking-wider text-xs">🚨 Same-day booking · +£{SAME_DAY_SURCHARGE}</div>
                <div className="mt-1">Same-day jobs carry a <strong>£{SAME_DAY_SURCHARGE} surcharge</strong> and you <strong>must WhatsApp us immediately after booking</strong> so we don't miss anything urgent.</div>
              </div>
            )}

            {!sameDay && isWeekend && suggestedWeekday && !weekdaySwap && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="weekday-nudge">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[color:var(--mv-navy)]">Save 10% with our weekday rate</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Switch to <strong>{format(suggestedWeekday, 'EEE, d MMM')}</strong> — a 10% weekday saver drops your total to around <strong>£{Math.round((price + extrasTotal) * 0.9)}</strong>. Weekdays also have better van availability.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={acceptWeekdaySwap}
                    data-testid="weekday-swap-btn"
                    className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  >
                    Swap to {format(suggestedWeekday, 'EEE')}
                  </button>
                </div>
              </div>
            )}

            {weekdaySwap && !sameDay && !isWeekend && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800" data-testid="weekday-applied-notice">
                🎉 <strong>Weekday saver applied</strong> — 10% off reflected in the total below.
              </div>
            )}

            {!bookChecked ? (
              <button
                type="button"
                onClick={runAvailabilityCheck}
                disabled={!bookDate || !bookTime || bookChecking}
                data-testid="book-check-btn"
                className="w-full h-11 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
              >
                {bookChecking ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Checking live calendar…</>
                ) : (
                  <><CalIcon className="w-4 h-4" /> Check availability</>
                )}
              </button>
            ) : (
              <>
                <div className="rounded-lg bg-green-50 border border-green-200 p-3" data-testid="book-available-banner">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-bold text-[color:var(--mv-navy)]">We have a booking slot available!</div>
                      <div className="text-slate-600 text-xs mt-0.5">
                        {format(bookDate, 'EEE, d MMM yyyy')} · <span className="font-semibold">{bookTime}</span> — provisionally held while you pay the deposit.
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-green-200">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Service you've asked for</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="flex items-start gap-2">
                        <Truck className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold text-[color:var(--mv-navy)]">{estimate.van_recommendation}</div>
                          <div className="text-slate-600">Service: {form.service}{form.property_size ? ` · ${form.property_size}` : ''}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Package className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold text-[color:var(--mv-navy)]">Crew & time</div>
                          <div className="text-slate-600">{estimate.hours_estimate} · {form.from_postcode} → {form.to_postcode}</div>
                        </div>
                      </div>
                    </div>
                    {Array.isArray(estimate.breakdown) && estimate.breakdown.length > 0 && (
                      <div className="mt-3 bg-white rounded-md border border-green-200 p-2.5">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">What's included</div>
                        <ul className="text-xs text-slate-700 space-y-0.5">
                          {estimate.breakdown.slice(0, 4).map((b, i) => (
                            <li key={i} className="flex justify-between gap-3">
                              <span className="truncate">{b.label}</span>
                              <span className="font-semibold text-[color:var(--mv-navy)]">{b.value}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        Final total
                        {sameDay && <span className="text-red-700 font-semibold"> (incl. £{SAME_DAY_SURCHARGE} same-day)</span>}
                        {extrasTotal > 0 && <span className="text-slate-700 font-semibold"> (incl. £{extrasTotal} extras)</span>}
                        {weekdaySaver > 0 && <span className="text-emerald-700 font-semibold"> (−£{weekdaySaver} weekday)</span>}
                      </span>
                      <span className="font-black text-[color:var(--mv-navy)] text-base">£{finalPrice}</span>
                    </div>
                  </div>

                  <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                    <strong>Heads-up:</strong> this slot is <strong>not held until we've checked your deposit has arrived</strong>. Pay the £{deposit} deposit in Step 2 below.
                  </div>

                  {sameDay && (
                    <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900" data-testid="sameday-arrival-notice">
                      <strong>Same-day arrival window:</strong> because we're pulling a van off the road for you, we have <strong>availability between {bookTime} and {addHours(bookTime, 6)}</strong>. We'll WhatsApp you a tighter ETA the moment the crew is assigned.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Step 2 — Deposit */}
      <div className="mt-6 rounded-2xl border-2 border-[color:var(--mv-orange)] bg-white overflow-hidden">
        <div className="bg-[color:var(--mv-navy)] text-white px-5 py-3 flex items-center justify-between">
          <div className="text-[11px] font-black tracking-[0.22em] uppercase text-[color:var(--mv-orange-2)]">
            Step 2 · Pay deposit by bank transfer
          </div>
          <div className="text-[11px] font-bold tracking-wider uppercase text-white/70">
            Required
          </div>
        </div>
        <div className="p-5">
          {/* Optional extras */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="extras-card">
            <div className="text-[10px] uppercase tracking-[0.22em] font-black text-[color:var(--mv-orange)]">Optional extras</div>
            <label className="mt-2 flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={extraOn}
                onChange={(e) => setExtraOn(e.target.checked)}
                data-testid="extra-toggle"
                className="mt-1 w-4 h-4 accent-[color:var(--mv-orange)]"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-[color:var(--mv-navy)] text-sm">{extraOption.label}</div>
                  <div className="font-black text-[color:var(--mv-navy)] text-sm whitespace-nowrap">+£{extraOption.price}</div>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{extraOption.blurb}</div>
              </div>
            </label>
          </div>

          <div className="mt-4 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Pay 20% deposit</div>
              <div className="font-display text-3xl sm:text-4xl font-extrabold text-[color:var(--mv-navy)] leading-none mt-1" data-testid="deposit-amount">
                £{deposit.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                Final total <strong className="text-[color:var(--mv-navy)]">£{finalPrice}</strong>
                {extrasTotal > 0 && <> · incl. £{extrasTotal} extras</>}
                {sameDay && <> · incl. £{SAME_DAY_SURCHARGE} same-day</>}
                {weekdaySaver > 0 && <> · −£{weekdaySaver} weekday saver</>}
                . Balance of <strong className="text-[color:var(--mv-navy)]">£{balance}</strong> due on moving day.
              </div>
            </div>
          </div>
          <DepositTransfer quoteId={estimate.id} date={bookDate ? toIsoDate(bookDate) : ''} time={bookTime} extras={selectedExtras.map(x => x.code)} weekdayDiscount={weekdaySwap && !sameDay && !isWeekend} ready={!!(bookDate && bookTime)} deposit={deposit} />
        </div>
      </div>

      <div className="mt-6">
        <ol className="space-y-2.5">
          <StepRow
            n={3}
            title="Questions? WhatsApp us (recommended)"
            hint="Fastest way to reach us — 24/7. Great for reschedules, extra items, access issues or just to say hi to the team."
            cta="Message on WhatsApp"
            href={whatsappUrl}
            variant="primary"
            icon={MessageCircle}
          />
        </ol>
        <div className="mt-3 text-center text-xs text-slate-500">
          Prefer a quick chat? <a href="tel:07985291097" className="font-semibold text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)] underline-offset-2 hover:underline">Call 07985 291097</a>
        </div>
      </div>

      <button onClick={onReset} className="w-full mt-3 text-center text-xs text-slate-500 hover:text-[color:var(--mv-orange)] font-semibold uppercase tracking-wider">
        Start a new quote
      </button>

      <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
        Subject to our <a href="/terms" className="underline hover:text-[color:var(--mv-orange)]">Terms &amp; Conditions</a>.
      </p>
    </div>
  );
};

/* ---------------- Small UI helpers ---------------- */

const StepLabel = ({ icon: Icon, title, small }) => (
  <div className={`flex items-center gap-2 ${small ? 'mb-2' : 'mb-3'}`}>
    <div className={`${small ? 'w-6 h-6' : 'w-7 h-7'} rounded-md bg-[color:var(--mv-navy)] text-white flex items-center justify-center`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className={`font-bold text-[color:var(--mv-navy)] ${small ? 'text-sm' : 'text-base'}`}>{title}</div>
  </div>
);

const AccessPills = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {ACCESS.map((a) => {
      const active = value === a;
      return (
        <button
          key={a}
          type="button"
          onClick={() => onChange(a)}
          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${active ? 'bg-[color:var(--mv-orange)] text-white border-[color:var(--mv-orange)]' : 'bg-white text-[color:var(--mv-navy)] border-slate-200 hover:border-slate-300'}`}
        >
          {a}
        </button>
      );
    })}
  </div>
);

const Toggle = ({ active, onClick, title, hint }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left p-3 rounded-lg border transition-all ${active ? 'border-[color:var(--mv-orange)] bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
  >
    <div className="flex items-center gap-2">
      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${active ? 'bg-[color:var(--mv-orange)] border-[color:var(--mv-orange)]' : 'border-slate-300'}`}>
        {active && <CheckCircle2 className="w-3 h-3 text-white" />}
      </div>
      <div className="font-bold text-sm text-[color:var(--mv-navy)]">{title}</div>
    </div>
    <div className="text-xs text-slate-500 mt-1 pl-6">{hint}</div>
  </button>
);

const StepRow = ({ n, title, hint, cta, href, variant = 'ghost', icon: Icon }) => {
  const isDisabled = variant === 'disabled';
  const btnClass =
    variant === 'primary' ? 'btn-primary' :
    variant === 'dark' ? 'bg-[color:var(--mv-navy)] text-white hover:bg-[#13315c] px-4 py-2 rounded-md font-bold text-sm inline-flex items-center gap-1.5 transition-colors' :
    variant === 'disabled' ? 'bg-slate-200 text-slate-400 px-4 py-2 rounded-md font-bold text-sm inline-flex items-center gap-1.5 cursor-not-allowed' :
    'btn-ghost-dark';
  const openNew = href.startsWith('http');
  return (
    <li className={`flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white ${isDisabled ? 'opacity-80' : ''}`}>
      <div className={`w-7 h-7 shrink-0 rounded-full ${isDisabled ? 'bg-slate-300' : 'bg-[color:var(--mv-orange)]'} text-white flex items-center justify-center font-bold text-sm`}>
        {n}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[color:var(--mv-navy)] text-sm">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
      </div>
      {isDisabled ? (
        <span className={`shrink-0 ${btnClass} text-xs whitespace-nowrap`} aria-disabled="true">
          <Icon className="w-3.5 h-3.5" /> {cta}
        </span>
      ) : (
        <a
          href={href}
          target={openNew ? '_blank' : undefined}
          rel={openNew ? 'noreferrer' : undefined}
          className={`shrink-0 ${btnClass} text-xs whitespace-nowrap`}
        >
          <Icon className="w-3.5 h-3.5" /> {cta}
        </a>
      )}
    </li>
  );
};

// Explicit text color prevents inheriting white from dark hero on mobile Safari
const TextField = (props) => (
  <input
    {...props}
    className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-[color:var(--mv-navy)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)] focus:border-transparent"
  />
);

const IconField = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
    <input
      {...props}
      className="h-11 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm text-[color:var(--mv-navy)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)] focus:border-transparent"
    />
  </div>
);

export default EstimateWizard;
